// 알람 소리 공용 모듈
// - 여러 소리 프리셋 정의
// - 선택된 소리 / 음소거 설정을 localStorage에 저장
// - 실제 알람 재생(playAlarmSound) 및 미리듣기(previewAlarmSound) 제공
//
// 기존에는 AlarmNotificationContext / EquipmentBoard / FactoryMachineOverview
// 3곳에 "삐삐삐 × 3주기" 소리가 중복 구현되어 있었으나 이 모듈로 통합했다.

// AudioContext 싱글턴 (브라우저 자동재생 정책상 첫 사용자 인터랙션에서 활성화)
let sharedAudioCtx = null;
function getAudioContext() {
  if (!sharedAudioCtx) {
    sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return sharedAudioCtx;
}

if (typeof window !== 'undefined') {
  const unlock = () => {
    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') ctx.resume();
    } catch {
      /* 무시 */
    }
  };
  document.addEventListener('click', unlock);
  document.addEventListener('keydown', unlock);
}

// 단일 비프 스케줄 (짧은 attack/release 엔벨로프로 클릭 잡음 방지)
function scheduleBeep(ctx, { start, freq, dur, type = 'square', peak = 0.3 }) {
  const t0 = ctx.currentTime + start;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.01);
  gain.gain.setValueAtTime(peak, t0 + Math.max(0.02, dur - 0.03));
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

// 주파수 스윕 스케줄 (사이렌용)
function scheduleSweep(ctx, { start, from, to, dur, peak = 0.3 }) {
  const t0 = ctx.currentTime + start;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(from, t0);
  osc.frequency.linearRampToValueAtTime(to, t0 + dur);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.02);
  gain.gain.setValueAtTime(peak, t0 + Math.max(0.03, dur - 0.05));
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

// 프리셋 정의: id → 재생 함수
const PRESETS = {
  // 단일 비프 (요청: 삐 1번) — 기본값
  single: (ctx) => {
    scheduleBeep(ctx, { start: 0, freq: 880, dur: 0.2 });
  },
  // 더블 비프 (삐-삐)
  double: (ctx) => {
    scheduleBeep(ctx, { start: 0, freq: 880, dur: 0.15 });
    scheduleBeep(ctx, { start: 0.25, freq: 880, dur: 0.15 });
  },
  // 삐삐삐 (기존 소리 1주기)
  triple: (ctx) => {
    [0, 0.35, 0.7].forEach((s) => scheduleBeep(ctx, { start: s, freq: 880, dur: 0.15 }));
  },
  // 부드러운 차임 (2음)
  chime: (ctx) => {
    scheduleBeep(ctx, { start: 0, freq: 660, dur: 0.3, type: 'sine', peak: 0.35 });
    scheduleBeep(ctx, { start: 0.28, freq: 990, dur: 0.4, type: 'sine', peak: 0.35 });
  },
  // 사이렌 (주파수 스윕 2회)
  siren: (ctx) => {
    scheduleSweep(ctx, { start: 0, from: 500, to: 1000, dur: 0.5 });
    scheduleSweep(ctx, { start: 0.5, from: 500, to: 1000, dur: 0.5 });
  },
};

// UI에서 사용할 프리셋 id 목록 (라벨은 i18n 키로 매핑)
export const ALARM_SOUND_IDS = ['single', 'double', 'triple', 'chime', 'siren'];
export const DEFAULT_ALARM_SOUND = 'single';

const SOUND_KEY = 'alarm-sound-id';
const MUTE_KEY = 'alarm-sound-muted';

export function getAlarmSoundId() {
  try {
    const v = localStorage.getItem(SOUND_KEY);
    return v && PRESETS[v] ? v : DEFAULT_ALARM_SOUND;
  } catch {
    return DEFAULT_ALARM_SOUND;
  }
}

export function setAlarmSoundId(id) {
  try {
    if (PRESETS[id]) localStorage.setItem(SOUND_KEY, id);
  } catch {
    /* 무시 */
  }
}

export function isAlarmMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setAlarmMuted(muted) {
  try {
    localStorage.setItem(MUTE_KEY, muted ? 'true' : 'false');
  } catch {
    /* 무시 */
  }
}

// 지정한 프리셋을 즉시 재생 (미리듣기 — 음소거 무시)
export function previewAlarmSound(id = getAlarmSoundId()) {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') ctx.resume();
    const preset = PRESETS[id] || PRESETS[DEFAULT_ALARM_SOUND];
    preset(ctx);
  } catch (e) {
    console.error('알람 소리 재생 실패:', e);
  }
}

// 실제 알람 발생 시 재생 — 음소거 상태면 재생하지 않음
export function playAlarmSound() {
  if (isAlarmMuted()) return;
  previewAlarmSound(getAlarmSoundId());
}
